import React from 'react';
import { ArrowUp } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useLocale } from '@/i18n/LocaleContext';
import { SOCIAL_LINKS, TelegramIcon, XIcon } from './Social';

export const Footer = () => {
  const { t, locale, setLocale, locales, supported } = useLocale();

  return (
    <footer data-testid="site-footer" className="border-t-4 border-primary bg-card/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
          <div className="max-w-md">
            <img
              src="/assets/00-brand/peipei-wordmark.png"
              alt={t.footer.wordmarkAlt}
              className="h-12 w-auto rounded-md"
              loading="lazy"
            />
            <p data-testid="footer-affiliation" className="mt-5 text-sm leading-relaxed text-muted-foreground">
              {t.footer.affiliation}
            </p>
            <p data-testid="footer-risk" className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {t.footer.risk}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={SOCIAL_LINKS.telegram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-telegram"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:border-primary/60"
              >
                <TelegramIcon className="h-4 w-4" />
                {t.social.telegramLabel}
              </a>
              <a
                href={SOCIAL_LINKS.x}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-x"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:border-primary/60"
              >
                <XIcon className="h-4 w-4" />
                {t.social.xLabel}
              </a>
            </div>
          </div>

          <div className="flex flex-col gap-5 md:items-end">
            <div className="w-full sm:w-64">
              <label htmlFor="footer-language-select" className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                {t.footer.languageLabel}
              </label>
              <Select value={locale} onValueChange={setLocale}>
                <SelectTrigger
                  id="footer-language-select"
                  data-testid="footer-language-select"
                  aria-label={t.footer.languageLabel}
                  className="bg-secondary border-border text-foreground"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {supported.map((code) => (
                    <SelectItem key={code} value={code} data-testid={`footer-language-option-${code}`}>
                      {locales[code].languageName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="footer-back-to-top"
              onClick={() => window.scrollTo({ top: 0 })}
              className="border-border bg-secondary text-foreground hover:bg-muted hover:text-primary gap-2 self-start md:self-end"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
              {t.footer.backToTop}
            </Button>
          </div>
        </div>

        <Separator className="my-8 bg-border" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p data-testid="footer-rights" className="text-xs text-muted-foreground">{t.footer.rights}</p>
          {/* Micro seal: tiny footer stamp only, never a logo */}
          <img
            src="/assets/06-stickers/micro-peipei-seal.png"
            alt={t.footer.sealAlt}
            data-testid="footer-micro-seal"
            className="h-8 w-8 opacity-80"
            loading="lazy"
          />
        </div>
      </div>
    </footer>
  );
};
